/**
 * VersionWatcher — detects new deployed builds and prompts the user to reload.
 *
 * Strategy: capture the *content-hashed* JS bundle filenames from the live
 * <script> tags in the current document on mount. Then every 60 seconds,
 * re-fetch `/index.html` with a cache-busting query string and parse the
 * fresh `<script src>` filenames out of it. When the set of bundle filenames
 * no longer matches what we booted with, we know a new build has been
 * deployed → show a non-blocking sonner toast with a "Reload" action.
 *
 * Why bundle filenames and not a version.json? Webpack/CRA already content-
 * hashes every bundle (`main.abc123.js`). When a new build ships, AT LEAST
 * ONE filename changes. This means we get version detection "for free" with
 * no build-script changes, no extra files to deploy, no extra env vars.
 *
 * Mount once in `App.js`. Idempotent — only one toast is ever shown per
 * detection cycle (until reloaded).
 */

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const POLL_INTERVAL_MS = 60_000; // 60s — light enough to not chatter, fast enough to feel responsive
const TOAST_ID = "new-version-available"; // stable id → de-dupes if effect fires twice in StrictMode

function extractBundleHashes(htmlText) {
  // Match <script src="/static/js/main.abc123.chunk.js"> across CRA / Vite builds.
  const matches = htmlText.match(/<script[^>]+src=["']([^"']+)["']/g) || [];
  return matches
    .map((m) => {
      const src = m.match(/src=["']([^"']+)["']/);
      return src ? src[1] : null;
    })
    .filter(Boolean)
    .filter((s) => s.includes("/static/")) // only application bundles, not 3rd-party CDN scripts
    .sort()
    .join("|");
}

function snapshotCurrentBundles() {
  if (typeof document === "undefined") return "";
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  return scripts
    .map((s) => s.getAttribute("src"))
    .filter((s) => s && s.includes("/static/"))
    .sort()
    .join("|");
}

export default function VersionWatcher() {
  const baselineRef = useRef(null);
  const alertedRef = useRef(false);

  useEffect(() => {
    baselineRef.current = snapshotCurrentBundles();
    // Bail if we can't snapshot the current bundles (e.g. during SSR / tests) —
    // there's nothing to compare against.
    if (!baselineRef.current) return undefined;

    let cancelled = false;

    const checkForUpdate = async () => {
      if (cancelled || alertedRef.current) return;
      try {
        const res = await fetch(`/index.html?_v=${Date.now()}`, {
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!res.ok) return; // 401/404 — silently skip this cycle, retry next tick
        const html = await res.text();
        const incoming = extractBundleHashes(html);
        if (!incoming) return; // parse failure — skip
        if (incoming !== baselineRef.current) {
          alertedRef.current = true;
          toast.message("A new version of RealtyVault Manager is available", {
            id: TOAST_ID,
            description: "Reload to get the latest features and fixes.",
            duration: Infinity, // sticky until user dismisses or clicks Reload
            icon: <RefreshCw className="w-4 h-4 text-emerald-600" />,
            action: {
              label: "Reload",
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // Network blips happen on flaky connections — fail silent, retry next tick.
      }
    };

    // First check after the initial poll interval (don't toast on app boot).
    const interval = setInterval(checkForUpdate, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return null;
}
